import { useState, useEffect } from 'react';
import { TrendingUp, Package, DollarSign, Star, Eye, ShoppingBag, Calendar } from 'lucide-react';
import api from '../utils/api';

const COIN_COLORS = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };

export default function SellerAnalytics() {
  const [data, setData] = useState(null);
  const [commission, setCommission] = useState(null);
  const [premium, setPremium] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchAll();
  }, [period]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [orders, comm, prem] = await Promise.all([
        api.get('/orders/my-sales'),
        api.get('/commission/my-records').catch(() => ({ data: { records: [], totals: {} } })),
        api.get('/commission/premium/my').catch(() => ({ data: { subscription: null } })),
      ]);

      const sales = orders.data.orders || [];
      const since = new Date();
      since.setDate(since.getDate() - period);
      const periodSales = sales.filter(o => new Date(o.createdAt) >= since);

      // Aggregate stats
      const completed = periodSales.filter(o => o.status === 'completed');
      const totalRevenueUSD = completed.reduce((s, o) => s + (o.totalUSD || 0), 0);
      const totalOrders = completed.length;
      const avgOrderUSD = totalOrders > 0 ? totalRevenueUSD / totalOrders : 0;

      // Daily revenue for chart
      const dailyMap = {};
      completed.forEach(o => {
        const day = new Date(o.createdAt).toISOString().split('T')[0];
        dailyMap[day] = (dailyMap[day] || 0) + (o.totalUSD || 0);
      });
      const daily = Object.entries(dailyMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-14); // last 14 days

      // Revenue by currency
      const byCurrency = {};
      completed.forEach(o => {
        if (!byCurrency[o.paymentCurrency]) byCurrency[o.paymentCurrency] = { count: 0, totalCrypto: 0, totalUSD: 0 };
        byCurrency[o.paymentCurrency].count++;
        byCurrency[o.paymentCurrency].totalCrypto += o.cryptoAmount || 0;
        byCurrency[o.paymentCurrency].totalUSD += o.totalUSD || 0;
      });

      // Status breakdown
      const statusMap = {};
      periodSales.forEach(o => { statusMap[o.status] = (statusMap[o.status] || 0) + 1; });

      setData({ sales: periodSales, completed, totalRevenueUSD, totalOrders, avgOrderUSD, daily, byCurrency, statusMap });
      setCommission(comm.data);
      setPremium(prem.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><div className="spinner" /></div>;

  const { totalRevenueUSD, totalOrders, avgOrderUSD, daily, byCurrency, statusMap, completed } = data || {};
  const totals = commission?.totals || {};
  const sub = premium?.subscription;
  const maxDaily = Math.max(...(daily?.map(([,v]) => v) || [1]), 1);

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mt-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Sora' }}>Analytics</h1>
          <p className="text-xs text-brand-muted">Your store performance</p>
        </div>
        <select className="input-dark text-sm w-32" value={period} onChange={e => setPeriod(parseInt(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Premium badge */}
      {sub && (
        <div className="p-3 rounded-xl mb-5 flex items-center gap-2" style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.25)' }}>
          <span className="text-lg">⭐</span>
          <div>
            <p className="text-sm font-bold text-brand-orange capitalize">{sub.plan} Plan Active</p>
            <p className="text-xs text-brand-muted">{sub.benefits?.reducedCommission}% commission reduction · {sub.benefits?.featuredSpots} featured slot{sub.benefits?.featuredSpots > 1 ? 's' : ''} · Valid until {new Date(sub.endDate).toDateString()}</p>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          { label: 'Total Revenue', value: `$${totalRevenueUSD?.toFixed(2) || '0.00'}`, icon: DollarSign, color: '#00C864' },
          { label: 'Completed Orders', value: totalOrders || 0, icon: ShoppingBag, color: '#FF6B00' },
          { label: 'Avg Order Value', value: `$${avgOrderUSD?.toFixed(2) || '0.00'}`, icon: TrendingUp, color: '#4DA2FF' },
          { label: 'Commission Paid', value: `$${totals.totalCommissionPaid?.toFixed(2) || '0.00'}`, icon: Package, color: '#F5A623' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
                <Icon size={16} style={{ color }} />
              </div>
              <span className="text-xs text-brand-muted">{label}</span>
            </div>
            <p className="text-xl font-bold" style={{ color, fontFamily: 'Sora' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Daily revenue bar chart */}
      {daily && daily.length > 0 && (
        <div className="p-4 rounded-2xl mb-5" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-sm" style={{ fontFamily: 'Sora' }}>Daily Revenue (last 14 days)</h2>
          </div>
          <div className="flex items-end gap-1 h-32">
            {daily.map(([date, value]) => {
              const height = Math.max((value / maxDaily) * 100, 4);
              return (
                <div key={date} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="absolute bottom-full mb-1 bg-brand-card border border-brand-border rounded px-1.5 py-0.5 text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    ${value.toFixed(2)}
                  </div>
                  <div className="w-full rounded-t-sm transition-all hover:opacity-80"
                    style={{ height: `${height}%`, background: 'linear-gradient(180deg,#FF6B00,#F5A623)' }} />
                  <span className="text-xs text-brand-muted" style={{ fontSize: '9px' }}>
                    {date.split('-').slice(1).join('/')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Revenue by currency */}
      {Object.keys(byCurrency || {}).length > 0 && (
        <div className="p-4 rounded-2xl mb-5" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          <h2 className="font-bold text-sm mb-3" style={{ fontFamily: 'Sora' }}>Revenue by Currency</h2>
          <div className="space-y-2">
            {Object.entries(byCurrency).sort(([,a],[,b]) => b.totalUSD - a.totalUSD).map(([coin, stats]) => {
              const pct = totalRevenueUSD > 0 ? (stats.totalUSD / totalRevenueUSD) * 100 : 0;
              return (
                <div key={coin}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-bold font-mono" style={{ color: COIN_COLORS[coin] || '#FF6B00' }}>{coin}</span>
                    <div className="flex gap-3 text-xs text-brand-muted">
                      <span>{stats.count} orders</span>
                      <span className="font-bold text-brand-text">${stats.totalUSD.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: '#1E1E2E' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: COIN_COLORS[coin] || '#FF6B00' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Order status breakdown */}
      {Object.keys(statusMap || {}).length > 0 && (
        <div className="p-4 rounded-2xl mb-5" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          <h2 className="font-bold text-sm mb-3" style={{ fontFamily: 'Sora' }}>Order Status Breakdown</h2>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(statusMap).map(([status, count]) => {
              const colors = { completed:'#00C864', pending:'#F5A623', disputed:'#FF3232', shipped:'#FF6B00', cancelled:'#6B6B8A' };
              return (
                <div key={status} className="p-2 rounded-xl text-center" style={{ background: '#0A0A0F', border: '1px solid #1E1E2E' }}>
                  <p className="text-lg font-bold" style={{ color: colors[status] || '#4DA2FF', fontFamily: 'Sora' }}>{count}</p>
                  <p className="text-xs text-brand-muted capitalize">{status}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Commission summary */}
      {totals.totalCommissionPaid > 0 && (
        <div className="p-4 rounded-2xl mb-5" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          <h2 className="font-bold text-sm mb-3" style={{ fontFamily: 'Sora' }}>Fee Summary (All Time)</h2>
          <div className="space-y-2 text-sm">
            {[
              ['Total Sales Revenue', `$${totals.totalReceived?.toFixed(2)}`,'#00C864'],
              ['Commission Paid', `$${totals.totalCommissionPaid?.toFixed(2)}`,'#F5A623'],
              ['Network Fees Paid', `$${totals.totalNetworkFee?.toFixed(2)}`,'#FF6B00'],
              ['Total Orders', totals.totalOrders, '#4DA2FF'],
            ].map(([label, value, color]) => (
              <div key={label} className="flex justify-between py-1" style={{ borderBottom: '1px solid #1E1E2E' }}>
                <span className="text-brand-muted">{label}</span>
                <span className="font-bold" style={{ color }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {totalOrders === 0 && (
        <div className="text-center py-16">
          <TrendingUp size={48} className="text-brand-muted mx-auto mb-4 opacity-30" />
          <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Sora' }}>No sales yet in this period</h3>
          <p className="text-brand-muted text-sm">Sales data will appear here once orders complete</p>
        </div>
      )}
    </div>
  );
}
